import { useCallback, useMemo, useState } from 'react';

/**
 * Klickbare Spaltenüberschriften für Listen, die ihren Datenbestand vollständig
 * geladen haben. Sortiert wird nach deutschen Regeln (Umlaute einsortiert,
 * Zahlen in Texten numerisch), Leerwerte stehen immer am Ende — egal in welche
 * Richtung sortiert wird, denn "leer" ist kein Wert, der vorne oder hinten gehört.
 */

const isEmpty = (value) => value == null || value === '';

const compareValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'boolean' || typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' });
};

/**
 * @param {Object} accessors  Spaltenschlüssel → Funktion, die den Sortierwert liefert
 * @param {Object} initial    { key, dir } — Startsortierung, dir 'asc' | 'desc'
 */
export default function useTableSort(accessors, initial = {}) {
  const [sort, setSort] = useState({
    key: initial.key || null,
    dir: initial.dir === 'desc' ? 'desc' : 'asc',
  });

  const toggleSort = useCallback((key) => {
    setSort((cur) => (
      cur.key === key
        ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  }, []);

  const sortRows = useCallback((rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const read = accessors?.[sort.key];
    if (!sort.key || typeof read !== 'function') return list;
    const factor = sort.dir === 'desc' ? -1 : 1;
    // Kopie: die Aufrufer geben oft direkt ihren State weiter.
    return [...list].sort((rowA, rowB) => {
      const a = read(rowA);
      const b = read(rowB);
      if (isEmpty(a) && isEmpty(b)) return 0;
      if (isEmpty(a)) return 1;
      if (isEmpty(b)) return -1;
      return compareValues(a, b) * factor;
    });
  }, [accessors, sort]);

  return useMemo(() => ({ sort, toggleSort, sortRows }), [sort, toggleSort, sortRows]);
}
